const UTIL = {};

const viewportUnitCache = {
  svh: { px: 0, width: -1 },
  lvh: { px: 0, width: -1 },
};

function measureViewportUnitPx(unit) {
  const probe = document.createElement('div');
  probe.style.cssText = `position:fixed;top:0;left:0;height:100${unit};width:0;visibility:hidden;pointer-events:none;`;
  document.documentElement.appendChild(probe);
  let h = probe.getBoundingClientRect().height;
  document.documentElement.removeChild(probe);
  if (!(h > 0)) {
    h = window.innerHeight || document.documentElement.clientHeight || 1;
  }
  return Math.max(1, h);
}

function cachedViewportUnit(unit) {
  const width = window.innerWidth || document.documentElement.clientWidth || 0;
  const cache = viewportUnitCache[unit];
  if (cache.px >= 1 && width === cache.width) {
    return cache.px;
  }
  cache.width = width;
  cache.px = measureViewportUnitPx(unit);
  return cache.px;
}

UTIL.svh = function () {
  return cachedViewportUnit('svh');
};

UTIL.lvh = function () {
  return cachedViewportUnit('lvh');
};

function invalidateViewportUnitCache() {
  viewportUnitCache.svh.px = 0;
  viewportUnitCache.svh.width = -1;
  viewportUnitCache.lvh.px = 0;
  viewportUnitCache.lvh.width = -1;
}

const layoutSettleMs = 180;
let layoutSettleTimer = null;
const layoutSettleListeners = [];

function flushLayoutSettle() {
  layoutSettleTimer = null;
  invalidateViewportUnitCache();
  for (let i = 0; i < layoutSettleListeners.length; i++) {
    layoutSettleListeners[i]();
  }
  try {
    window.dispatchEvent(new CustomEvent('util:layoutsettle'));
  } catch (err) {}
}

function scheduleLayoutSettle() {
  if (layoutSettleTimer) {
    window.clearTimeout(layoutSettleTimer);
  }
  layoutSettleTimer = window.setTimeout(flushLayoutSettle, layoutSettleMs);
}

UTIL.onLayoutSettle = function (fn) {
  if (typeof fn === 'function') {
    layoutSettleListeners.push(fn);
  }
  return function unsubscribe() {
    const i = layoutSettleListeners.indexOf(fn);
    if (i >= 0) {
      layoutSettleListeners.splice(i, 1);
    }
  };
};

if (typeof window !== 'undefined') {
  window.addEventListener('resize', scheduleLayoutSettle);
  window.addEventListener('orientationchange', scheduleLayoutSettle);
}

function resolveNode(el) {
  if (!el) {
    return null;
  }
  if (typeof el === 'string') {
    return document.querySelector(el);
  }
  if (el.nodeType === 1) {
    return el;
  }
  return null;
}

UTIL.isElementVisible = function (el, viewportHeight) {
  const node = resolveNode(el);
  if (!node || typeof node.getBoundingClientRect !== 'function') {
    return false;
  }
  const vh = viewportHeight || UTIL.svh();
  const rect = node.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < vh;
};

UTIL.hasFunctionByPath = function (path, rootObj) {
  if (typeof path !== 'string') {
    return null;
  }
  const cleanPath = String(path).trim();
  if (!cleanPath) {
    return null;
  }

  const parts = cleanPath.split('.');
  let scope = rootObj || window;
  for (let i = 0; i < parts.length - 1; i++) {
    if (scope == null || typeof scope !== 'object') {
      return null;
    }
    scope = scope[parts[i]];
  }
  if (scope == null) {
    return null;
  }

  const methodName = parts[parts.length - 1];
  const fn = scope[methodName];
  if (typeof fn !== 'function') {
    return null;
  }

  return {
    context: scope,
    fn,
  };
};

UTIL.invokeFunctionByPath = function (path, args, rootObj) {
  const ref = UTIL.hasFunctionByPath(path, rootObj);
  if (!ref) {
    return false;
  }
  ref.fn.apply(ref.context, Array.isArray(args) ? args : []);
  return true;
};

UTIL.attrBreakpointSmPx = 768;
UTIL.attrBreakpointPx = 992;
UTIL.attrBreakpointXlPx = 2400;

function attrNode(el) {
  return resolveNode(el);
}

function firstNonEmpty(parts) {
  for (let i = 0; i < parts.length; i++) {
    if (parts[i] !== '') {
      return parts[i];
    }
  }
  return '';
}

UTIL.pickBreakpointValue = function (raw, widthPx) {
  if (raw == null) {
    return null;
  }
  const parts = String(raw).split('|').map((part) => part.trim());
  const width = widthPx != null ? widthPx : (window.innerWidth || document.documentElement.clientWidth || 0);
  let chosen;
  if (parts.length >= 4) {
    if (width < UTIL.attrBreakpointSmPx) {
      chosen = parts[0];
    } else if (width < UTIL.attrBreakpointPx) {
      chosen = parts[1];
    } else if (width < UTIL.attrBreakpointXlPx) {
      chosen = parts[2];
    } else {
      chosen = parts[3];
    }
  } else if (parts.length === 3) {
    if (width < UTIL.attrBreakpointSmPx) {
      chosen = parts[0];
    } else if (width < UTIL.attrBreakpointPx) {
      chosen = parts[1];
    } else {
      chosen = parts[2];
    }
  } else if (parts.length === 2) {
    chosen = width < UTIL.attrBreakpointPx ? parts[0] : parts[1];
  } else {
    chosen = parts[0] || '';
  }
  if (chosen === '') {
    chosen = firstNonEmpty(parts);
  }
  return chosen;
};

UTIL.cssLengthToPx = function (raw, contextEl) {
  if (raw == null) {
    return 0;
  }
  let s = String(raw).trim();
  if (!s) {
    return 0;
  }
  let sign = 1;
  if (s.charAt(0) === '-') {
    sign = -1;
    s = s.slice(1).trim();
  } else if (s.charAt(0) === '+') {
    s = s.slice(1).trim();
  }
  const n = parseFloat(s);
  if (!Number.isFinite(n)) {
    return 0;
  }
  const unit = s.replace(/^[+-]?[\d.]+/, '').trim().toLowerCase();
  let px = 0;
  const node = resolveNode(contextEl) || document.documentElement;
  if (unit === '' || unit === 'px') {
    px = n;
  } else if (unit === 'rem') {
    px = n * (parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16);
  } else if (unit === 'em') {
    px = n * (parseFloat(window.getComputedStyle(node).fontSize) || 16);
  } else if (unit === 'svh') {
    px = (n / 100) * UTIL.svh();
  } else if (unit === 'lvh') {
    px = (n / 100) * UTIL.lvh();
  } else if (unit === 'vh' || unit === 'dvh') {
    px = (n / 100) * UTIL.svh();
  } else if (unit === 'vw') {
    px = (n / 100) * (window.innerWidth || document.documentElement.clientWidth || 1);
  } else {
    px = n;
  }
  return sign * px;
};

function parseScaleExpression(raw, contextEl) {
  const s = String(raw || '').trim();
  if (!s) {
    return null;
  }
  const clampMatch = s.match(/^clamp\(\s*([^,]+),\s*(.+),\s*([^)]+)\s*\)$/i);
  if (clampMatch) {
    const lo = parseScaleExpression(clampMatch[1], contextEl);
    const mid = parseScaleExpression(clampMatch[2], contextEl);
    const hi = parseScaleExpression(clampMatch[3], contextEl);
    if (lo == null || mid == null || hi == null) {
      return null;
    }
    return Math.min(hi, Math.max(lo, mid));
  }
  const divParts = s.split('/');
  if (divParts.length === 2) {
    const a = UTIL.cssLengthToPx(divParts[0], contextEl);
    const b = UTIL.cssLengthToPx(divParts[1], contextEl);
    if (!(b > 0)) {
      return null;
    }
    return a / b;
  }
  if (/^-?[\d.]+$/.test(s)) {
    const constant = parseFloat(s);
    return Number.isFinite(constant) ? constant : null;
  }
  const refPx = UTIL.cssLengthToPx(s, contextEl);
  if (refPx > 0) {
    return UTIL.svh() / refPx;
  }
  return null;
}

UTIL.scrollVh = function (contextEl) {
  const svh = UTIL.svh();
  const node = attrNode(contextEl) || document.documentElement;
  let host = null;
  if (node && typeof node.closest === 'function') {
    host = node.closest('[data-scroll-vh-max]');
  }
  if (!host && document.documentElement?.hasAttribute('data-scroll-vh-max')) {
    host = document.documentElement;
  }
  if (!host && document.body?.hasAttribute('data-scroll-vh-max')) {
    host = document.body;
  }
  let maxRaw = host ? UTIL.attr(host, 'data-scroll-vh-max') : '900px';
  if (maxRaw == null || String(maxRaw).trim() === '') {
    maxRaw = '900px';
  }
  let maxPx = UTIL.cssLengthToPx(maxRaw, node);
  if (!(maxPx > 0)) {
    maxPx = 900;
  }
  return Math.max(1, Math.min(svh, maxPx));
};

UTIL.scrollScale = function (contextEl) {
  const node = attrNode(contextEl) || document.documentElement;
  let host = null;
  if (node && typeof node.closest === 'function') {
    host = node.closest('[data-scroll-scale]');
  }
  if (!host && document.documentElement?.hasAttribute('data-scroll-scale')) {
    host = document.documentElement;
  }
  if (!host && document.body?.hasAttribute('data-scroll-scale')) {
    host = document.body;
  }
  const raw = host ? UTIL.attr(host, 'data-scroll-scale') : null;
  let scale = parseScaleExpression(raw, node);
  if (scale == null) {
    scale = UTIL.scrollVh(node) / 900;
  }
  const minRaw = host ? UTIL.attr(host, 'data-scroll-scale-min') : null;
  const maxRaw = host ? UTIL.attr(host, 'data-scroll-scale-max') : null;
  let min = minRaw != null && minRaw !== '' ? parseFloat(minRaw) : 0.85;
  let max = maxRaw != null && maxRaw !== '' ? parseFloat(maxRaw) : 2;
  if (!Number.isFinite(min)) {
    min = 0.85;
  }
  if (!Number.isFinite(max)) {
    max = 2;
  }
  if (scale < min) {
    return min;
  }
  if (scale > max) {
    return max;
  }
  return scale;
};

function lengthUnit(raw) {
  let s = String(raw || '').trim();
  if (s.charAt(0) === '-' || s.charAt(0) === '+') {
    s = s.slice(1).trim();
  }
  return s.replace(/^[+-]?[\d.]+/, '').trim().toLowerCase();
}

UTIL.scrollLengthPx = function (raw, contextEl) {
  let px = UTIL.cssLengthToPx(raw, contextEl);
  const unit = lengthUnit(raw);
  const viewportRelative = unit === 'svh' || unit === 'lvh' || unit === 'vh' || unit === 'dvh' || unit === 'vw';
  if (!viewportRelative) {
    px *= UTIL.scrollScale(contextEl);
  }
  return px;
};

UTIL.lengthToVh = function (raw, contextEl) {
  if (raw == null || String(raw).trim() === '') {
    return 0;
  }
  const px = UTIL.cssLengthToPx(raw, contextEl);
  const unit = lengthUnit(raw);
  const svh = Math.max(1, UTIL.svh());
  const viewportRelative = unit === 'svh' || unit === 'lvh' || unit === 'vh' || unit === 'dvh';
  if (viewportRelative) {
    return (px / svh) * 100;
  }
  return (px / 900) * 100;
};

UTIL.attr = function (el, attrName, widthPx) {
  const node = attrNode(el);
  if (!node || typeof node.getAttribute !== 'function' || !node.hasAttribute(attrName)) {
    return null;
  }
  return UTIL.pickBreakpointValue(node.getAttribute(attrName), widthPx);
};

UTIL.attrNumber = function (el, attrName, fallback, widthPx) {
  const str = UTIL.attr(el, attrName, widthPx);
  if (str == null || str === '') {
    return fallback !== undefined ? fallback : null;
  }
  const value = parseFloat(str);
  if (!Number.isFinite(value)) {
    return fallback !== undefined ? fallback : null;
  }
  return value;
};

UTIL.attrScrollLength = function (el, attrName, fallback, widthPx) {
  const str = UTIL.attr(el, attrName, widthPx);
  if (str == null || str === '') {
    return fallback !== undefined ? fallback : null;
  }
  return UTIL.scrollLengthPx(str, el);
};

export default UTIL;
